import {
  Container,
  Box,
  Header,
  Button,
  Grid,
  SpaceBetween,
  Link,
  SegmentedControl,
  StatusIndicator,
  Icon,
  Input,
  Alert,
  Table,
  Pagination,
  ExpandableSection,
  FormField,
  Autosuggest,
  TokenGroup,
} from '@cloudscape-design/components';

import { useCallback, useEffect, useState } from 'react';
import { DropdownStatusProps } from '@cloudscape-design/components/internal/components/dropdown-status';
import * as Sentry from '@sentry/react';

export type Suggestion = {
  label: string;
  description: string;
  value: string;
  placeId: string;
  tags: string[];
};

export type AutoCompleteSearchProps = {
  loading?: boolean;
  selectedItems: Suggestion[];
  setSelectedItems: (items: Suggestion[]) => void;
  searchDisabled?: boolean;
  hideSearch?: boolean;
  placeholder?: string;
};

const SKIP_TAGS = ['establishment', 'point_of_interest', 'tourist_attraction'];

function formatTag(tag: string): string {
  const words = tag.toLowerCase().replaceAll('_', ' ').split(' ');
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}

function filterHierarchicalTags(tags: string[]): string[] {
  // Filter out unwanted tags first.
  const filtered = tags.filter((tag) => !SKIP_TAGS.includes(tag));
  // Format all tags to a consistent style.
  const formatted = filtered.map(formatTag);

  // Sort tags by descending number of words (more specific tags first).
  formatted.sort((a, b) => b.split(' ').length - a.split(' ').length);

  const finalTags: string[] = [];
  for (const tag of formatted) {
    // Check if the current tag is contained as a whole word in any of the finalTags.
    // Here we simply check if the lowercased tag exists in the lowercased final tag.
    // For more robust matching, you could use regex with word boundaries.
    const isSubset = finalTags.some((existing) =>
      new RegExp(`\\b${tag.toLowerCase()}\\b`).test(existing.toLowerCase()),
    );
    if (!isSubset) {
      finalTags.push(tag);
    }
  }
  return finalTags;
}

export default function AutoCompleteSearch(props: AutoCompleteSearchProps) {
  const { loading, selectedItems, setSelectedItems, searchDisabled, hideSearch } = props;
  const [name, setName] = useState<string>('');
  const [options, setOptions] = useState<Suggestion[]>([]);
  // Status can be 'pending', 'loading', 'finished', or 'error'
  const [status, setStatus] = useState<DropdownStatusProps.StatusType | undefined>('pending');
  const [sessionToken, setSessionToken] = useState<
    google.maps.places.AutocompleteSessionToken | undefined
  >(undefined);

  useEffect(() => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }
    const sessionToken = new window.google.maps.places.AutocompleteSessionToken();
    setSessionToken(sessionToken);
  }, []);

  // Function to load suggestions using Google Places Autocomplete.
  const loadSuggestions = useCallback(
    async (inputValue: string) => {
      inputValue = inputValue.trim();
      if (!inputValue) {
        setOptions([]);
        setStatus('pending');
        return;
      }

      // Ensure the Google API is loaded.
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error('Google Maps JavaScript API is not loaded');
        Sentry.captureException('Google Maps JavaScript API is not loaded');
        setStatus('error');
        return;
      }

      let localSessionToken = sessionToken;
      if (!localSessionToken) {
        localSessionToken = new window.google.maps.places.AutocompleteSessionToken();
        setSessionToken(localSessionToken);
      }

      setStatus('loading');
      try {
        const predictions =
          await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: inputValue,
            sessionToken: localSessionToken,
            // Restrict to businesses (establishments)
            includedPrimaryTypes: ['establishment'],
          });

        // Map the predictions to an array of option objects.
        const suggestions =
          predictions.suggestions
            .filter((prediction) => prediction.placePrediction)
            .map(
              (prediction) =>
                ({
                  value: prediction.placePrediction?.placeId ?? '',
                  placeId: prediction.placePrediction?.placeId ?? '',
                  label: prediction.placePrediction?.mainText?.text ?? '',
                  description: prediction.placePrediction?.secondaryText?.text ?? '',
                  tags: filterHierarchicalTags(prediction.placePrediction?.types ?? []),
                }) as Suggestion,
            ) ?? [];
        // Filter out suggestions that are already selected.
        const filteredSuggestions = suggestions.filter(
          (suggestion) => !selectedItems.some((item) => item.placeId === suggestion.placeId),
        );
        setOptions(filteredSuggestions);
        // You might set 'finished' if you expect no further pagination.
        setStatus('finished');
      } catch (error) {
        console.error('Error loading suggestions', error);

        Sentry.captureException(error, {
          tags: {
            inputValue,
          },
        });
        setOptions([]);
        setStatus('error');
        return;
      }
    },
    [setOptions, setStatus, sessionToken],
  );

  return (
    <div>
      <SpaceBetween direction="vertical" size="s">
        {!hideSearch && (
          <Autosuggest
            className="pt-2"
            disabled={loading || searchDisabled}
            placeholder={props.placeholder ?? 'Enter a business name...'}
            autoFocus={false}
            ariaLabel="Autosuggest for business name"
            empty={name.trim() ? <Box variant="p">No matches found</Box> : undefined}
            filteringType="manual"
            onChange={({ detail }) => {
              setName(detail.value);
            }}
            // The statusType prop can be set using our local status state.
            statusType={status}
            // onLoadItems is triggered to load suggestions when needed.
            onLoadItems={({ detail }) => {
              loadSuggestions(detail.filteringText);
            }}
            value={name}
            // Options provided to the autosuggest component are the suggestions.
            options={options}
            loadingText="Loading suggestions..."
            errorText="Error loading suggestions, please try again."
            // finishedText="No more suggestions."
            recoveryText="Try again"
            onSelect={async ({ detail }) => {
              const suggestion = options.find((option) => option.value === detail.value);
              if (suggestion) {
                setName(name);
                if (!selectedItems.some((item) => item.placeId === suggestion.placeId)) {
                  setOptions((options) =>
                    options.filter((option) => option.placeId !== suggestion.placeId),
                  );
                  // TODO consider loading status here
                  setSelectedItems([...selectedItems, suggestion]);
                  const place = new google.maps.places.Place({
                    id: suggestion.placeId,
                  });
                  try {
                    await place.fetchFields({ fields: ['formattedAddress'] });
                    suggestion.description = place.formattedAddress ?? suggestion.description;
                    setSelectedItems([...selectedItems, suggestion]);
                  } catch (error) {
                    console.error('Error loading place details', error);
                  }
                }
              }
            }}
          />
        )}
        <TokenGroup
          onDismiss={({ detail: { itemIndex } }) => {
            setSelectedItems([
              ...selectedItems.slice(0, itemIndex),
              ...selectedItems.slice(itemIndex + 1),
            ]);
          }}
          items={selectedItems}
          readOnly={loading}
        />
      </SpaceBetween>
      <div id="hidden-map" style={{ display: 'none' }} />
    </div>
  );
}
